<script>
import Card from '@/components/ui/Card.vue';
import * as AdsV4Api from '@/api/advertisement-v4';
import lang from '@/mixins/lang';
import { truncate } from '@/utils/strings';
import MultiSelect from '../common/MultiSelect.vue';
import DatePickerInput from '@/components/common/DatePickerInput.vue';
import { mapActions, mapGetters } from 'vuex';
import { APPS } from '@/store/types';
import { createRangeUntilNow } from '@/utils/date';

const DATE_FORMAT = 'X';
const LAST_WEEK = createRangeUntilNow(1, 'weeks', DATE_FORMAT);

export default {
  name: 'ReportGenerateForm',
  components: { Card, MultiSelect, DatePickerInput },
  mixins: [lang],
  props: {
    initialValue: {
      type: Object,
      default: () => ({}),
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filters: {
        campaigns: [],
        dateRange: [...LAST_WEEK],
      },
      campaignsLoading: false,
    };
  },
  computed: {
    ...mapGetters({ apps: APPS }),
    campaignsDropdownItems() {
      return this.campaigns?.map(c => ({
        label: truncate(c.name, 19),
        value: c.uuid,
      }));
    },
    datePickerFormat: () => DATE_FORMAT,
    datePickerRanges() {
      return [
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_TWO_DAYS'),
          value: createRangeUntilNow(2, 'days'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_WEEK'),
          value: createRangeUntilNow(1, 'weeks'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_MONTH'),
          value: createRangeUntilNow(1, 'months'),
        },
        {
          label: this.$t('APP.STATS.FILTERS.RANGE.LAST_THREE_MONTHS'),
          value: createRangeUntilNow(3, 'months'),
        },
      ];
    },
  },
  watch: {
    initialValue: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.filters = {
          campaigns: [...value.campaigns],
          dateRange:
            value.start && value.end ? [value.start, value.end] : LAST_WEEK,
        };
      },
    },
  },
  created() {
    if (!this.apps?.length) this.fetchApps();
  },
  methods: {
    ...mapActions({ fetchApps: APPS }),
    emitGenerateReport() {
      this.$emit('submit', {
        campaigns: this.filters.campaigns,
        start: this.filters.dateRange[0],
        end: this.filters.dateRange[1],
      });
    },
    emitDownloadReport() {
      this.$emit('submit', {
        campaigns: this.filters.campaigns,
        start: this.filters.dateRange[0],
        end: this.filters.dateRange[1],
        export: true,
      });
    },
  },
};
</script>

<template>
  <Card>
    <div class="flex-row flex-sm-column align-sm-start gap-sm align-middle mb">
      <div class="reporting-filters__label">
        {{ $t('ADVERTISEMENT.CAMPAIGNS.TITLE') }}
      </div>

      <div class="flex-grow full-width">
        <MultiSelect
          v-model="filters.campaigns"
          :items="campaignsDropdownItems"
          :loading="campaignsLoading"
          :placeholder="$t('ADVERTISEMENT.REPORTING.SELECT_CAMPAIGNS')"
          filterable
        />
      </div>
    </div>

    <div class="flex-row flex-sm-column justify-spaced gap-md">
      <!-- format=X means unix timestamp  -->
      <div class="flex-row flex-sm-column align-sm-start gap-sm align-middle">
        <div class="reporting-filters__label">
          {{ $t('ADVERTISEMENT.REPORTING.REPORT_DATE') }}
        </div>

        <DatePickerInput
          v-model="filters.dateRange"
          :locale="lang"
          :ranges="datePickerRanges"
          :format="datePickerFormat"
          class="reporting-page__date-picker"
        />
      </div>

      <div class="flex-row align-middle gap-sm">
        <div>
          <ElButton
            type="primary"
            icon="el-icon-s-data"
            :disabled="!filters.campaigns?.length"
            @click="emitGenerateReport"
          >
            {{ $t('ADVERTISEMENT.REPORTING.GENERATE_REPORT') }}
          </ElButton>
        </div>

        <div>
          <ElButton
            style="margin-right: 0.5rem;"
            icon="el-icon-download"
            :disabled="!filters.campaigns?.length"
            @click="emitDownloadReport"
          />
        </div>
      </div>
    </div>
  </Card>
</template>
